import { formatDate } from '@helpers';

export const ITEM_COLUMNS = [
  {
    accessorKey: 'name',
    footer: 'Total',
    header: 'Bundle #',
    id: 'name',
    size: 350,
  },
  {
    accessorKey: 'total_credits',
    footer: '',
    footerAccessor: 'total_credits',
    header: 'Purchased',
    id: 'total_credits',
    size: 100,
  },
  {
    accessorKey: 'credits_used',
    footer: '',
    footerAccessor: 'total_credits_used',
    header: 'Used',
    id: 'credits_used',
    size: 100,
  },
  {
    accessorKey: 'available_credits',
    footer: '',
    footerAccessor: 'total_available_credits',
    header: 'Remaining',
    id: 'available_credits',
    size: 100,
  },
  {
    accessorKey: 'credits_in_flight',
    footer: '',
    footerAccessor: 'total_credits_in_flight',
    header: 'In Flight',
    id: 'credits_in_flight',
    size: 100,
  },
  {
    accessorKey: 'estimated_end_date',
    footer: '-',
    header: 'Expiration',
    id: 'estimated_end_date',
    size: 100,
  },
];

/**
 * transforms and formats credit bundles data
 * @param {Object} response
 * @return {Object} columns and data for credit bundles table
 */
export const transformCreditBundles = (response) => {
  const columns = ITEM_COLUMNS.map((column) => {
    // in v8, 0 automatically renders an empty string --> if 0, actually display a 0.
    const footerValue = column.footerAccessor
      ? (response[column.footerAccessor] ?? 0)
      : (column.footer ?? 0);
    return {
      ...column,
      footer: () => footerValue,
    };
  });

  return {
    columns,
    data: response?.results,
  };
};

/**
 * sanitize email preferences data
 * @param {Object} response
 * @returns {Object}
 */
export const transformEmailPreferences = (response) => {
  return {
    emailPreferences: {
      comments: response?.COMMENTS ?? false,
      schedules: response?.SCHEDULES ?? false,
      share: response?.SHARE ?? false,
    },
  };
};

/**
 * sanitize user data
 * @param {Object} response
 * @returns {Object}
 */
export const transformUser = (response) => {
  if (!response) return {};
  const { emailPreferences } = transformEmailPreferences(response?.email_preferences) || {};
  return {
    baseAccountUser: response?.sso_user,
    clientInfo: {
      id: response?.client_info?.id,
      inFlightCredits: response?.client_info?.in_flight_credits,
      name: response?.client_info?.name,
      onHoldCredits: response?.client_info?.on_hold_credits,
      size: response?.client_info?.size,
      subscriptions: response?.client_info?.subscriptions || [],
      totalCredits: response?.client_info?.total_credits,
    },
    email: response?.email,
    emailPreferences,
    engagementLevel: response?.engagement_level,
    firstName: response?.first_name,
    id: response?.id,
    isNewUser: response?.is_new_user,
    lastName: response?.last_name,
    name: response?.username,
    permissions: {
      canAccessAskWhy: response?.permissions?.can_access_askwhy
        ? {
            canPayWithCard: response?.permissions?.can_access_askwhy?.can_pay_with_card,
            canPayWithCredits: response?.permissions?.can_access_askwhy?.can_pay_with_credits,
            canPayWithInvoice: response?.permissions?.can_access_askwhy?.can_pay_with_invoice,
            canPayWithLicense: response?.permissions?.can_access_askwhy?.can_pay_with_license,
          }
        : false,
      canAccessBrand: response?.permissions?.can_access_brand,
      canAccessCreditBundles: response?.permissions?.can_access_settings_credit_bundle,
      canAccessDataWave: response?.permissions?.can_access_data_wave,
      canAccessGroups: response?.permissions?.can_access_groups,
      canAccessHomepage: response?.permissions?.can_access_homepage,
      canAccessNewItem: response?.permissions?.can_access_new_item,
      canAccessPortfolio: response?.permissions?.can_access_portfolio,
      canAccessPromo: response?.permissions?.can_access_promo,
      canAccessSurveySubscription: response?.permissions?.can_access_survey_subscription,
      canAutomateReports: response?.permissions?.can_create_schedules,
      canBypassMaintenance: response?.permissions?.can_bypass_maintenance,
      canCreateSurveys: response?.permissions?.can_create_surveys,
      canEditSurveySubscription: response?.permissions?.can_edit_survey_subscription,
      canLoginAsProxy: response?.permissions?.can_login_as_proxy,
      canShareFolders: response?.permissions?.can_share_folders,
      canShareGroups: response?.permissions?.can_share_groups,
      canShareReports: response?.permissions?.can_share_reports,
      canShareReportsPublicly: response?.permissions?.can_share_reports_publicly,
      canUpdateUserCredits: response?.permissions?.can_access_credit_management_editor,
      canUseAIGenerationQuestionnaire: response?.permissions?.can_allow_ai_question_generation,
    },
    persona: {
      discipline: response?.discipline,
      industry: response?.industry,
      jobTitle: response?.job_title,
      lastUpdated: response?.persona_last_updated,
      proficiency: response?.proficiency,
    },
    surveyInfo: {
      clientCreditBundleLastUpdated: formatDate(
        response?.survey_info?.client_credit_bundle_last_updated
      ),
      clientCredits: response?.survey_info?.client_survey_credits_remaining || 0,
      promoCredits: response?.survey_info?.user_survey_promotional_credits_remaining || 0,
      userCredits: response?.survey_info?.user_survey_credits_remaining || 0,
    },
  };
};

/**
 * transforms and formats sharable users data
 * @param {Object} response
 * @return {Object} combines first_name last_name object values under the display key
 */
export const transformUsersData = (response) => {
  return response?.results?.map((user) => {
    return {
      display: user?.first_name + ' ' + user?.last_name,
      id: user?.id,
    };
  });
};
